import type { App } from 'vue'
import type { Router } from 'vue-router'
import * as Sentry from '@sentry/vue'
import { onCLS, onFCP, onINP, onLCP, onTTFB, type Metric } from 'web-vitals'

const AUTH_STACK = 'vue3'

function reportWebVital(metric: Metric) {
  const value =
    metric.name === 'CLS' ? metric.value : Math.round(metric.value)

  Sentry.withScope((scope) => {
    scope.setTag('auth_stack', AUTH_STACK)
    scope.setTag('web_vital', metric.name)
    scope.setContext('web_vital', {
      name: metric.name,
      value: metric.value,
      rating: metric.rating,
      delta: metric.delta,
      id: metric.id,
      navigationType: metric.navigationType,
    })

    Sentry.startSpan(
      {
        name: `web-vital.${metric.name}`,
        op: 'ui.webvital',
        attributes: {
          'web_vital.name': metric.name,
          'web_vital.rating': metric.rating,
        },
      },
      (span) => {
        span?.setAttribute('web_vital.value', metric.value)
        Sentry.setMeasurement(
          metric.name.toLowerCase(),
          value,
          metric.name === 'CLS' ? '' : 'millisecond',
        )
      },
    )
  })
}

export function initWebVitals() {
  onLCP(reportWebVital)
  onFCP(reportWebVital)
  onTTFB(reportWebVital)
  onINP(reportWebVital)
  onCLS(reportWebVital)
}

export function initSentry(app: App, router: Router) {
  const dsn = 'https://91cb95178e493aea69ee7ac750bad90f@sentry.coral.club/10'

  const release =
    import.meta.env.VITE_APP_RELEASE ||
    `auth-vue3@${import.meta.env.VITE_APP_COMMIT || 'local'}`
  const environment =
    import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE

  Sentry.init({
    app,
    dsn,
    release,
    environment,
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.replayIntegration({
        maskAllText: false,
        maskAllInputs: false,
      }),
    ],
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.25,
    replaysOnErrorSampleRate: 0.25,
    tracePropagationTargets: ['localhost', /^\//],
    ignoreErrors: [
      /ym is not defined/,
      /Network Error/,
      /Can't find variable: ym/,
      /undefined is not an object \(evaluating 'e\.geometry\.location'\)/,
      /Cannot read properties of undefined \(reading 'location'\)/,
    ],
    denyUrls: [
      /local\/templates\/cc_new\/script/,
      /local\/templates\/cc_new\//,
      /maps\/api\/js/,
    ],
    initialScope: {
      tags: {
        auth_stack: AUTH_STACK,
        commit: import.meta.env.VITE_APP_COMMIT || 'local',
      },
    },
  })

  initWebVitals()
}
